import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppSettingsProvider } from "./context/AppSettingsContext";

// Pages
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Dashboard } from "./pages/Dashboard";
import { Customers } from "./pages/Customers";
import { CustomerDetail } from "./pages/CustomerDetail";
import { Jobs } from "./pages/Jobs";
import { NewJob } from "./pages/NewJob";
import { EditJob } from "./pages/EditJob";
import { JobDetail } from "./pages/JobDetail";
import { Settings } from "./pages/Settings";
import { AdminRates } from "./pages/AdminRates";
import { AdminExtras } from "./pages/AdminExtras";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminMaterials } from "./pages/AdminMaterials";
import { AdminPresets } from "./pages/AdminPresets";
import { AdminTemplate } from "./pages/AdminTemplate";
import { AdminInvoiceTemplate } from "./pages/AdminInvoiceTemplate";
import { AdminBranding } from "./pages/AdminBranding";
import { AdminExport } from "./pages/AdminExport";
import RoomDrawing from "./pages/RoomDrawing";
import RoomSurvey from "./pages/RoomSurvey";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                        <span className="text-xl font-bold text-white">PS</span>
                    </div>
                    <p className="text-slate-500">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
    const { isAdmin, loading } = useAuth();
    
    if (loading) {
        return null;
    }
    
    if (!isAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

function AppRoutes() {
    const { isAuthenticated } = useAuth();
    
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/login" 
                element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
            />
            <Route 
                path="/register" 
                element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />} 
            />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/customers" element={
                <ProtectedRoute><Customers /></ProtectedRoute>
            } />
            <Route path="/customers/:id" element={
                <ProtectedRoute><CustomerDetail /></ProtectedRoute>
            } />
            <Route path="/jobs" element={
                <ProtectedRoute><Jobs /></ProtectedRoute>
            } />
            <Route path="/jobs/new" element={
                <ProtectedRoute><NewJob /></ProtectedRoute>
            } />
            <Route path="/jobs/:id/edit" element={
                <ProtectedRoute><EditJob /></ProtectedRoute>
            } />
            <Route path="/jobs/:id" element={
                <ProtectedRoute><JobDetail /></ProtectedRoute>
            } />
            <Route path="/jobs/:id/add-room" element={
                <ProtectedRoute><RoomSurvey /></ProtectedRoute>
            } />
            <Route path="/jobs/:id/edit-room/:roomId" element={
                <ProtectedRoute><RoomSurvey /></ProtectedRoute>
            } />
            <Route path="/settings" element={
                <ProtectedRoute><Settings /></ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
                <ProtectedRoute><AdminRoute><Settings /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/rates" element={
                <ProtectedRoute><AdminRoute><AdminRates /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/materials" element={
                <ProtectedRoute><AdminRoute><AdminMaterials /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/extras" element={
                <ProtectedRoute><AdminRoute><AdminExtras /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/users" element={
                <ProtectedRoute><AdminRoute><AdminUsers /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/presets" element={
                <ProtectedRoute><AdminRoute><AdminPresets /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/template" element={
                <ProtectedRoute><AdminRoute><AdminTemplate /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/invoice-template" element={
                <ProtectedRoute><AdminRoute><AdminInvoiceTemplate /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/branding" element={
                <ProtectedRoute><AdminRoute><AdminBranding /></AdminRoute></ProtectedRoute>
            } />
            <Route path="/admin/export" element={
                <ProtectedRoute><AdminRoute><AdminExport /></AdminRoute></ProtectedRoute>
            } />

            {/* Standalone Prototype */}
            <Route path="/room-drawing" element={<RoomDrawing />} />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppSettingsProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster 
                        position="top-center" 
                        richColors 
                        closeButton
                        toastOptions={{
                            className: 'font-body',
                            duration: 3000
                        }}
                    />
                </BrowserRouter>
            </AppSettingsProvider>
        </AuthProvider>
    );
}

export default App;
